import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion, AnimationDefinition } from "framer-motion";

/**
 * LogoBars — the codelumen four-bar mark, treated as a semantic representation
 * of the four-layer system (Design · Product · Controls · Execution).
 *
 * Reusable across:
 *   • Progressive scroll indicators (variant="progress")
 *   • Nav / hover flashes           (variant="sequence")
 *   • Loading affordances           (variant="loading")
 *   • Static idle marks             (variant="idle")
 *
 * Design language: extremely subtle, architectural, warm bloom. Matches the
 * mobile menu bars already established in the header (widths 2/3/5/7) so the
 * component is truly a shared visual system, not a bespoke decoration.
 *
 * Respects `prefers-reduced-motion` by disabling per-bar cascades and glow
 * pulses (state changes still apply — bars just cut instantly).
 */

export type LogoBarsVariant = "idle" | "sequence" | "progress" | "loading" | "interactive";
export type LogoBarsTone = "light" | "dark";
export type LogoBarsSize = "xs" | "sm" | "md" | "lg";
export type LogoBarsGlowTone = "brand" | "neutral";

interface LogoBarsProps {
  variant?: LogoBarsVariant;
  /** For variant="progress": how many bars (0–4) are lit. */
  progress?: number;
  /** For variant="interactive": which bar is active (0–3), "all", or null. */
  activeBar?: number | "all" | null;
  /** For variant="sequence": bumping this key replays the illumination once. */
  triggerKey?: number | string | null;
  /** Renders bars in charcoal ("dark") or stone/white ("light"). Default: light. */
  tone?: LogoBarsTone;
  /** Keeps the established motion while allowing neutral-only surfaces to avoid the warm bloom. */
  glowTone?: LogoBarsGlowTone;
  size?: LogoBarsSize;
  className?: string;
  ariaLabel?: string;
}

// Bar widths mirror the codelumen barcode logo (thin → thick).
const BAR_WIDTHS = [2, 3, 5, 7] as const;

const SIZE_MAP: Record<LogoBarsSize, { height: number; gap: number; scale: number }> = {
  xs: { height: 14, gap: 3, scale: 0.6 },
  sm: { height: 20, gap: 3, scale: 0.85 },
  md: { height: 28, gap: 4, scale: 1 },
  lg: { height: 40, gap: 5, scale: 1.35 },
};

// Warm architectural bloom (Louis Poulsen tungsten). Clear enough to read as
// a feedback signal, still restrained enough for the menu's quiet-luxury tone.
const WARM_GLOW =
  "drop-shadow(0 0 6px rgba(255,176,110,0.7)) drop-shadow(0 0 18px rgba(255,148,60,0.32))";
const COOL_GLOW =
  "drop-shadow(0 0 5px rgba(255,255,255,0.55)) drop-shadow(0 0 14px rgba(255,255,255,0.18))";

const IDLE_OPACITY_LIGHT = 0.2;
const IDLE_OPACITY_DARK = 0.28;
const DIM_OPACITY = 0.16;

const EASE = [0.22, 1, 0.36, 1] as const;

const LogoBars = ({
  variant = "idle",
  progress = 0,
  activeBar = null,
  triggerKey = null,
  tone = "light",
  glowTone = "brand",
  size = "md",
  className = "",
  ariaLabel = "codelumen",
}: LogoBarsProps) => {
  const reduce = useReducedMotion();
  const { height, gap, scale } = SIZE_MAP[size];
  const barColor = tone === "light" ? "hsl(var(--stone))" : "hsl(var(--charcoal))";
  const glow = glowTone === "neutral" ? COOL_GLOW : WARM_GLOW;
  const idleOpacity = tone === "light" ? IDLE_OPACITY_LIGHT : IDLE_OPACITY_DARK;

  // ── sequence / loading state ────────────────────────────────────────────
  const [seqLit, setSeqLit] = useState<number>(-1); // -1 = none lit
  const sequenceLock = useRef(false);

  // One-shot sequence on triggerKey change.
  // Robust against React strict double-invoke, rapid re-taps, and Reduce
  // Motion: we always run the 4-step cascade and always release the lock in
  // the cleanup (not just via a delayed timeout).
  useEffect(() => {
    if (variant !== "sequence" || triggerKey == null) return;
    sequenceLock.current = true;

    // Reduce Motion: this is a brief, intentional acknowledgement of a nav
    // tap (essential feedback, not ambient decoration), so we keep the
    // cascade — just slightly slower and without the scaleY bump. See the
    // per-bar transition below where we also enforce a minimum fade under
    // reduce so the lit state is perceptible.
    const stepMs = reduce ? 180 : 125;
    const holdMs = 500;
    const fadeMs = 620;

    const timers: number[] = [];
    for (let i = 0; i < 4; i++) {
      timers.push(window.setTimeout(() => setSeqLit(i), i * stepMs));
    }
    timers.push(window.setTimeout(() => setSeqLit(-1), 4 * stepMs + holdMs));
    const unlockAt = 4 * stepMs + holdMs + fadeMs;
    timers.push(
      window.setTimeout(() => {
        sequenceLock.current = false;
      }, unlockAt)
    );

    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      // Always release the lock and reset lit state so the next trigger fires
      // even if this effect run was torn down early (strict-mode double
      // invoke, rapid re-tap, parent re-render during accordion scroll).
      sequenceLock.current = false;
      setSeqLit(-1);
    };
  }, [triggerKey, variant, reduce]);


  // Infinite loading loop
  useEffect(() => {
    if (variant !== "loading") return;
    if (reduce) {
      setSeqLit(3);
      return;
    }
    let i = 0;
    setSeqLit(0);
    const id = window.setInterval(() => {
      i = (i + 1) % 5; // 0,1,2,3, reset
      setSeqLit(i === 4 ? -1 : i);
    }, 220);
    return () => window.clearInterval(id);
  }, [variant, reduce]);

  // ── derived per-bar state ───────────────────────────────────────────────
  const barStates = useMemo(() => {
    return BAR_WIDTHS.map((_, i) => {
      let lit = false;
      let dim = false;
      let partial: number | null = null; // 0..1 for fractional fill of active bar
      if (variant === "progress") {
        if (i + 1 <= progress) {
          lit = true;
        } else if (i < progress) {
          // fractional: e.g. progress = 1.4 → bar 0 full, bar 1 at 0.4
          partial = Math.max(0, Math.min(1, progress - i));
        }
      } else if (variant === "sequence" || variant === "loading") {
        lit = seqLit >= 0 && i <= seqLit;
      } else if (variant === "interactive") {
        if (activeBar === "all") lit = true;
        else if (typeof activeBar === "number") {
          lit = i === activeBar;
          dim = activeBar !== null && i !== activeBar;
        }
      }
      return { lit, dim, partial };
    });
  }, [variant, progress, seqLit, activeBar]);

  const anySignal =
    variant === "progress"
      ? progress > 0
      : variant === "interactive"
        ? activeBar !== null
        : seqLit >= 0;

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className={`inline-flex items-end ${className}`}
      style={{ height, gap: `${gap}px` }}
    >
      {BAR_WIDTHS.map((w, i) => {
        const { lit, dim, partial } = barStates[i];
        const hasPartial = partial !== null && partial > 0;
        const targetOpacity = lit
          ? 1
          : hasPartial
            ? 1
            : dim
              ? DIM_OPACITY
              : anySignal
                ? DIM_OPACITY
                : idleOpacity;
        const animate: AnimationDefinition = {
          opacity: targetOpacity,
          filter: lit ? glow : "drop-shadow(0 0 0px rgba(0,0,0,0))",
          scaleY: lit && !reduce ? 1.08 : 1,
        };
        // Keep every bar physically present. Opacity carries the dim → lit
        // sequence so the mark never visually collapses into a one-frame flash.
        return (
          <motion.span
            key={i}
            className="rounded-[0.5px] block relative overflow-hidden"
            style={{
              width: w * scale,
              height: "72%",
              backgroundColor: hasPartial ? "transparent" : barColor,
              border: hasPartial ? `1px solid ${barColor}` : undefined,
              willChange: "opacity, filter, transform",
            }}
            initial={false}
            animate={animate}
            transition={{
              // Under reduce we still fade briefly (~250ms) so the lit state
              // is perceptible on iOS with Reduce Motion enabled.
              duration: reduce ? 0.25 : lit ? 0.28 : 0.5,
              ease: EASE,
            }}
          >
            {hasPartial && (
              <motion.span
                className="absolute inset-x-0 bottom-0 block"
                style={{ backgroundColor: barColor }}
                initial={false}
                animate={{ height: `${Math.round(partial! * 100)}%` }}
                transition={{ duration: reduce ? 0 : 0.25, ease: EASE }}
              />
            )}
          </motion.span>
        );
      })}
    </div>
  );
};

export default LogoBars;
